import { I18n } from "@assets/strings";
import React, { useState } from "react";
import styled from "styled-components";
import { Media } from "@domain/media/media";
import { BigPlusButton } from "@ui/components/buttons/bigPlusButton";
import { AssetManagerIcon, CameraIcon, DeleteIcon, YoutubeIcon } from "@ui/components/icons";
import { Row } from "@ui/components/layout";
import { LoadingBar } from "@ui/components/loadingBar";
import { AssetBrowserModal } from "@ui/components/media/assetBrowser/assetBrowserModal";
import { TooshareVideoPlayer } from "@ui/components/media/tooshareVideoPlayer";
import { VideoPlayer } from "@ui/components/media/videoPlayer";
import { FileUpload } from "@ui/components/mediaEditor/fileUpload";
import { Menu } from "@ui/components/menus/menu";
import { MenuRow } from "@ui/components/menus/menuRow";
import { SimpleMenuContent } from "@ui/components/menus/simpleMenuContent";
import { useModalTools } from "@ui/modals/modalContext";
import { fadeIn } from "@ui/styles/keyframes";
import { gap } from "@ui/styles/utils";

interface VideoPresentationEditorProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
	presentationAsset: { url?: string; video?: Media };
	onChange: (presesntationAsset: { video?: Media; url?: string }) => void;
}

export const VideoPresentationEditor: React.FC<VideoPresentationEditorProps> = ({
	presentationAsset,
	onChange,
	...htmlProps
}) => {
	const [uploadProgress, setUploadProgress] = useState<number | null>(null);
	const [onEditUrl, setEditUrl] = useState<boolean>(false);
	const { createModal } = useModalTools();

	return (
		<Container {...htmlProps}>
			{presentationAsset && presentationAsset?.video && (
				<>
					<StyledTooshareVideo asset={presentationAsset.video} />
					<DeleteImageIcon onClick={() => onChange({ video: undefined, url: undefined })}>
						<DeleteIcon />
					</DeleteImageIcon>
				</>
			)}
			{onEditUrl || presentationAsset?.url ? (
				<StyledSection>
					<InputWrapper>
						<YoutubeIcon />
						<input
							placeholder={I18n.t("course.editor.section.video.placeholder")}
							value={presentationAsset?.url}
							onChange={(e) => onChange({ video: undefined, url: e.currentTarget.value })}
						/>
					</InputWrapper>
				</StyledSection>
			) : null}
			{presentationAsset?.url && (
				<>
					<StyledVideoPlayer source={presentationAsset.url} />
					<DeleteImageIcon
						onClick={() => {
							setEditUrl(false);
							onChange({ video: undefined, url: undefined });
						}}
					>
						<DeleteIcon />
					</DeleteImageIcon>
				</>
			)}

			<Menu
				renderMenuContent={(close) => (
					<SimpleMenuContent>
						<MenuRow>
							<FileUpload
								type="video"
								onUploadStart={() => {
									setEditUrl(false);
									setUploadProgress(0);
									close();
								}}
								onUploadProgress={setUploadProgress}
								onUpload={(media) => {
									onChange({ video: media, url: undefined });
									setUploadProgress(null);
								}}
								onError={() => {
									return setUploadProgress(null);
								}}
							>
								{() => (
									<Row gap={5} align="center">
										<CameraIcon /> <div>{I18n.t("course.editor.section.video.new")}</div>
									</Row>
								)}
							</FileUpload>
						</MenuRow>
						<MenuRow
							onClick={() => {
								setEditUrl(false);
								close();
								createModal((closeModal) => (
									<AssetBrowserModal
										allowedTypes={["video/*"]}
										close={(assets) => {
											closeModal();
											const asset = assets?.get(0);
											if (asset) {
												onChange({ video: asset, url: undefined });
											}
										}}
									/>
								));
							}}
						>
							<AssetManagerIcon /> <span>{I18n.t("asset_manager.title")}</span>
						</MenuRow>
						<StyledMenuRow
							onClick={() => {
								close();
								setEditUrl(true);
							}}
						>
							<YoutubeIcon /> <div>{I18n.t("course.editor.section.youtube.new")}</div>
						</StyledMenuRow>
					</SimpleMenuContent>
				)}
				menuWrapperStyle={{
					left: "50%",
					right: "initial",
					top: "50%",
					transform: "translate(-50%, 16px)",
				}}
			>
				{uploadProgress !== null ? (
					<LoadingBar progress={uploadProgress} />
				) : !presentationAsset?.video && !presentationAsset?.url ? (
					<BigPlusButton label={I18n.t("shareroom.editor.presentation.video.new")} />
				) : null}
			</Menu>
		</Container>
	);
};

const Container = styled.div`
	${gap(20)};
	position: relative;
	width: 100%;
`;

const StyledTooshareVideo = styled(TooshareVideoPlayer)`
	width: 100%;
	height: 328px;
	.quality-control {
		display: none;
	}
`;

const StyledVideoPlayer = styled(VideoPlayer)`
	height: 328px;
	margin-top: 10px;
`;

const DeleteImageIcon = styled.div`
	position: absolute;
	right: 10px;
	top: 10px;
	cursor: pointer;

	@media (pointer: fine) {
		display: none;
	}

	${Container}:hover & {
		display: block;
	}

	animation: ${fadeIn} 0.2s linear;
	transition: transform 0.2s ease-in-out;
	&:hover {
		transform: rotate(90deg);
	}
`;

const StyledMenuRow = styled(MenuRow)`
	cursor: pointer;
`;

const InputWrapper = styled.div`
	display: flex;
	align-items: center;
	svg {
		width: 20px;
		height: 20px;
		margin-right: 10px;
	}

	input {
		font-size: 0.75rem;
		flex: 1;
		border: none;
		outline: none;
		text-overflow: ellipsis;
		padding-right: 10px;
	}
`;

const StyledSection = styled.div`
	border: solid 1px #eaebef;
	padding: 10px 16px;
	border-radius: 13px;
`;
